/**
 * The single description of "where the user asked to go and who asked".
 * Every navigation source in setup and the mapping workspace produces one of
 * these and hands it to the navigation guard; nothing navigates on its own.
 */

export type NavigationSource =
  | 'embeddedTab'
  | 'hostRibbonTab'
  | 'hostRibbonStep'
  | 'hostRibbonProfile'
  | 'workflowStep'
  | 'mappingBack'
  | 'summaryReturnToSetup'
  | 'profileSelection'
  | 'profileLoad'
  | 'programmaticTab'
  | 'validationTarget';

export type NavigationDestinationKind = 'workspaceTab' | 'workflowStep' | 'setupBack' | 'profile';

export interface NavigationIntent {
  /** Unique per request so a stale completion can be recognised and dropped. */
  id: string;
  source: NavigationSource;
  kind: NavigationDestinationKind;
  /** Stable destination key: tab key, screen name, or profile key. */
  target: string;
  /** Human-readable destination name used in the unsaved-changes copy. */
  label?: string;
}

/** A destination plus the control that asked for it — the guard assigns the id. */
export type NavigationRequest = Omit<NavigationIntent, 'id'>;

let intentSequence = 0;

export const createNavigationIntent = (request: NavigationRequest): NavigationIntent => {
  intentSequence += 1;
  return { ...request, id: `nav-${intentSequence}` };
};

/**
 * Workflow steps live in the shared layout, so their destination is built here
 * rather than in a feature: the web stepper and the host ribbon must produce
 * the identical request for the same step.
 */
export const workflowStepDestination = (
  source: NavigationSource,
  screen: string,
  label?: string
): NavigationRequest => ({ source, kind: 'workflowStep', target: screen, label });

/** Two intents point at the same place regardless of which control raised them. */
export const isSameNavigationDestination = (
  left: NavigationIntent | null | undefined,
  right: NavigationIntent | null | undefined
): boolean => Boolean(left) && Boolean(right) && left!.kind === right!.kind && left!.target === right!.target;

const FallbackDestinationLabel: Record<NavigationDestinationKind, string> = {
  workspaceTab: 'another tab',
  workflowStep: 'another step',
  setupBack: 'the previous step',
  profile: 'another profile',
};

export const describeNavigationDestination = (intent: NavigationIntent): string => {
  if (intent.kind === 'setupBack' || !intent.label) {
    return FallbackDestinationLabel[intent.kind];
  }

  switch (intent.kind) {
    case 'workspaceTab':
      return `the ${intent.label} tab`;
    case 'workflowStep':
      return `the ${intent.label} step`;
    default:
      return `the ${intent.label} profile`;
  }
};

export interface UnsavedNavigationCopy {
  title: string;
  message: string;
  saveLabel: string;
  discardLabel: string;
  cancelLabel: string;
  /** Named on screen while the save the user asked for is still running. */
  savingLabel: string;
  /** Named on screen while the baseline the user asked for is being restored. */
  discardingLabel: string;
}

export const resolveUnsavedNavigationCopy = (intent: NavigationIntent): UnsavedNavigationCopy => ({
  title: 'Unsaved setup changes',
  message:
    `Your setup has unsaved changes. Save them before continuing to ${describeNavigationDestination(intent)}, `
    + 'or discard the changes.',
  saveLabel: 'Save & Continue',
  discardLabel: 'Discard changes',
  cancelLabel: 'Stay here',
  savingLabel: 'Saving your setup…',
  discardingLabel: 'Restoring your last saved setup…',
});
